import { G } from '@mobily/ts-belt';
import Hashids from 'hashids';
import { z } from 'zod';

import { createZodHashIdParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

let hashids: Hashids;

export function configureHashIds(alphabet: string, salt: string) {
  // hashids.encode(Number.MAX_SAFE_INTEGER).length === 11
  const minLength = 12;

  hashids = new Hashids(salt, minLength, alphabet);
  return hashids;
}

export function encodeHashId(id: number): string;
export function encodeHashId(id: number | undefined | null): string | undefined;
export function encodeHashId(id: null | undefined): undefined;
export function encodeHashId(id: number | null | undefined): string | undefined {
  if (G.isNullable(id)) {
    return undefined;
  }

  return hashids.encode(id);
}

export function decodeHashId(hashId: string): number;
export function decodeHashId(hashId: string | undefined): number | null;
export function decodeHashId(hashId: null | undefined): null;
export function decodeHashId(hashId: string | null | undefined): number | null;
export function decodeHashId(hashId: string | null | undefined): number | null {
  if (G.isNullable(hashId)) {
    return null;
  }
  if (!Number.isNaN(Number(hashId))) {
    return Number(hashId);
  }
  const decoded = hashids.decode(hashId);

  if (decoded.length === 1) {
    const [id] = decoded;

    if (typeof id === 'bigint') {
      throw new Error('BigInt not supported');
    }

    return id;
  }
  return null;
}

// need to make sure this one is added on requests or inbound data (requests, params)
export const ZodHashedIdRequest = ZodTrimmedString.transform((arg, ctx) => {
  try {
    const decodedHashId = decodeHashId(arg);
    if (G.isNullable(decodedHashId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid ID',
        fatal: true,
      });
      return z.NEVER;
    }
    return decodedHashId;
  } catch (err) {
    // if we forgotten to initialize hashids
    if (err instanceof TypeError) {
      throw err;
    } else {
      // do not throw 500 when a not supported character is input into hashid decode
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid ID',
        fatal: true,
      });
      return z.NEVER;
    }
  }
}).meta({ override: { type: 'number' } });

// need to make sure this is only added on response or outbound data (responses)
export const ZodHashedIdResponse = z
  .number()
  .int()
  .transform((arg) => {
    return encodeHashId(arg);
  })
  .meta({ override: { type: 'string' } });

export type HashedIdRequest = z.TypeOf<typeof ZodHashedIdRequest>;
export type HashedIdResponse = z.TypeOf<typeof ZodHashedIdResponse>;

export const ZodHashedIDPathParam = createZodHashIdParamsValidator(
  z.object({
    id: ZodHashedIdRequest,
  }),
);
